import {
  LEDGER_TYPE_MAP,
  type FxFigure,
  type LedgerAccountType,
  type TableExportColumn,
  type TableExportDoc,
  type TableExportRow,
  type TableExportSheet,
} from '@goyone/shared';
import { api, qs } from './api';
import { fxMoney, rateText } from './currency';

/**
 * القوائم المالية — أنواعُها، وتحويلُها مستندَ تصدير (lib/tableExport).
 *
 * التصديرُ يُبنى من البيانات نفسِها التي تعرضها الشاشة، بالأرقام كما جاءت
 * من الخادم، فلا يخالف الملفُّ ما رآه المستخدم.
 */

export interface TrialRow {
  id: string;
  code: string;
  name: string;
  type: LedgerAccountType;
  debit: number;
  credit: number;
  balance: number;
  /** للحساب بعملة أجنبية: رصيده بعملته وما يساويه بسعر تاريخ الميزان. */
  fx?: { currency: string; amount: number; rate: number | null; value: number | null } | null;
}

export interface StatementLine {
  id: string;
  code: string;
  name: string;
  level: number;
  isGroup: boolean;
  /** بالجنيه — والحساب بعملة أجنبية بسعر تاريخ القائمة. */
  amount: number;
  fx?: FxFigure | null;
  children: StatementLine[];
}

export interface TrialBalanceData {
  rows: TrialRow[];
  totals: { debit: number; credit: number; balanced: boolean };
}

export interface IncomeStatementData {
  revenue: { lines: StatementLine[]; total: number };
  expenses: { lines: StatementLine[]; total: number };
  grossProfit: number;
  netProfit: number;
  /** فروق تقييم العملة غير المحققة عن المدة، والصافي بعدها. */
  fxRevaluation?: number;
  netProfitAfterFx?: number;
  margin: number | null;
}

export interface BalanceSheetData {
  assets: { lines: StatementLine[]; total: number };
  liabilities: { lines: StatementLine[]; total: number };
  equity: { lines: StatementLine[]; total: number };
  netProfit: number;
  /** فروق تقييم العملة غير المحققة حتى تاريخ الميزانية — ضمن حقوق الملكية. */
  fxRevaluation?: number;
  totalEquityAndLiabilities: number;
  balanced: boolean;
  difference: number;
}

export type FinancialStatement = 'income' | 'balance' | 'trial';

export const STATEMENT_TITLES: Record<FinancialStatement, string> = {
  income: 'قائمة الدخل',
  balance: 'الميزانية',
  trial: 'ميزان المراجعة',
};

/** اسم سطر فروق التقييم في القوائم. */
export const FX_REVALUATION_LABEL = 'فروق تقييم عملة غير محققة';

/** المبلغ بالعملة الأجنبية وسعره — «12,345 $ × 50.25». */
export function fxText(fx: { currency: string; amount: number; rate: number | null } | null | undefined): string | null {
  if (!fx) return null;
  return `${fxMoney(fx.amount, fx.currency)} × ${rateText(fx.rate)}`;
}

/** سطور إضافية بعد الحسابات: فروق التقييم إن وُجدت. */
export function fxExtra(amount: number | undefined): Array<{ label: string; amount: number }> {
  return amount ? [{ label: FX_REVALUATION_LABEL, amount }] : [];
}

/** الشجرةُ صفوفًا مسطّحةً بترتيبها — كلُّ حسابٍ تحت أبيه. */
export function flattenLines(lines: StatementLine[]): StatementLine[] {
  const rows: StatementLine[] = [];
  const walk = (nodes: StatementLine[]): void => {
    for (const node of nodes) {
      rows.push(node);
      walk(node.children);
    }
  };
  walk(lines);
  return rows;
}

/* ==========================================================================
   مستند التصدير
   ========================================================================== */

/** التاريخُ من حقل التاريخ إلى ما يقرؤه الخادم — كما تفعل الشاشة تمامًا. */
const isoDay = (ymd: string) => new Date(ymd).toISOString();

const ACCOUNT_COLUMNS: TableExportColumn[] = [
  { key: 'code', label: 'الكود', width: 12 },
  { key: 'name', label: 'الحساب', width: 44, indent: true },
  { key: 'amount', label: 'المبلغ', kind: 'money' },
];

/** عمود العملة الأجنبية — يُضاف لقسم فيه حساب بعملة أجنبية فقط. */
const FX_COLUMN: TableExportColumn = { key: 'fx', label: 'بالعملة الأجنبية × السعر', width: 26 };

const hasFx = (lines: StatementLine[]) => flattenLines(lines).some((l) => l.fx);

/** الشجرةُ صفوفًا: كلُّ حسابٍ مزاحٌ بمستواه، والتجميعيُّ بارز. */
function accountRows(lines: StatementLine[], extra: Array<{ label: string; amount: number }> = []): TableExportRow[] {
  return [
    ...flattenLines(lines).map((l) => ({
      code: l.code,
      name: l.name,
      amount: l.amount,
      fx: fxText(l.fx),
      _level: l.level,
      _style: l.isGroup ? ('group' as const) : undefined,
    })),
    ...extra.map((e) => ({ code: null, name: e.label, amount: e.amount })),
  ];
}

const section = (
  title: string,
  lines: StatementLine[],
  total: number,
  extra: Array<{ label: string; amount: number }> = [],
) => ({
  title,
  columns: hasFx(lines) ? [...ACCOUNT_COLUMNS, FX_COLUMN] : ACCOUNT_COLUMNS,
  rows: accountRows(lines, extra),
  totals: { name: `إجمالي ${title}`, amount: total + extra.reduce((s, e) => s + e.amount, 0) },
});

const summary = (rows: Array<{ label: string; amount: number; strong?: boolean }>) => ({
  title: 'الخلاصة',
  columns: ACCOUNT_COLUMNS,
  rows: rows.map((r) => ({ code: null, name: r.label, amount: r.amount, _style: r.strong ? ('total' as const) : undefined })),
  totals: null,
});

export function incomeSheet(d: IncomeStatementData, from: string, to: string): TableExportSheet {
  return {
    title: STATEMENT_TITLES.income,
    period: { from, to },
    sections: [
      section('الإيرادات', d.revenue.lines, d.revenue.total),
      section('المصروفات', d.expenses.lines, d.expenses.total),
      summary([
        { label: 'إجمالي الإيرادات', amount: d.revenue.total },
        { label: 'إجمالي المصروفات', amount: d.expenses.total },
        { label: 'مجمل الربح (بعد التكلفة المباشرة)', amount: d.grossProfit },
        { label: 'صافي الربح', amount: d.netProfit, strong: true },
        ...(d.fxRevaluation
          ? [
              { label: FX_REVALUATION_LABEL, amount: d.fxRevaluation },
              { label: 'صافي الربح بعد فروق التقييم', amount: d.netProfitAfterFx ?? d.netProfit, strong: true },
            ]
          : []),
      ]),
    ],
    notes: [
      ...(d.margin !== null ? [`هامش صافي الربح ${d.margin}٪`] : []),
      ...(d.fxRevaluation
        ? ['فروق التقييم: تغيّر قيمة الأرصدة بالعملات الأجنبية بين أول المدة وآخرها بسعر الصرف — غير مرحَّلة في الدفاتر.']
        : []),
    ],
  };
}

export function balanceSheetSheet(d: BalanceSheetData, to: string): TableExportSheet {
  return {
    title: STATEMENT_TITLES.balance,
    period: { label: `كما في ${to}` },
    sections: [
      section('الأصول', d.assets.lines, d.assets.total),
      section('الالتزامات', d.liabilities.lines, d.liabilities.total),
      section('حقوق الملكية', d.equity.lines, d.equity.total, [
        { label: 'صافي ربح المدة', amount: d.netProfit },
        ...fxExtra(d.fxRevaluation),
      ]),
      summary([
        { label: 'إجمالي الأصول', amount: d.assets.total, strong: true },
        { label: 'إجمالي الالتزامات وحقوق الملكية', amount: d.totalEquityAndLiabilities, strong: true },
      ]),
    ],
    notes: [
      d.balanced
        ? 'الميزانية متوازنة: الأصول تساوي الالتزامات وحقوق الملكية.'
        : `الميزانية غير متوازنة بفرق ${Math.abs(d.difference).toFixed(2)} — راجع القيود المرحَّلة.`,
      ...(d.fxRevaluation !== undefined && [d.assets, d.liabilities].some((s) => hasFx(s.lines))
        ? ['الحسابات بالعملات الأجنبية مقوَّمة بالجنيه بسعر تاريخ الميزانية، والفرق عن قيمتها الدفترية في «فروق تقييم عملة غير محققة».']
        : []),
    ],
  };
}

export function trialSheet(d: TrialBalanceData, to: string): TableExportSheet {
  const anyFx = d.rows.some((r) => r.fx);
  return {
    title: STATEMENT_TITLES.trial,
    period: { label: `أرصدة تراكمية حتى ${to}` },
    sections: [
      {
        columns: [
          { key: 'code', label: 'الكود', width: 12 },
          { key: 'name', label: 'الحساب', width: 40 },
          { key: 'type', label: 'النوع', width: 14 },
          { key: 'debit', label: 'مدين', kind: 'money' },
          { key: 'credit', label: 'دائن', kind: 'money' },
          { key: 'balance', label: 'الرصيد', kind: 'money' },
          ...(anyFx
            ? ([
                { key: 'fx', label: 'بالعملة الأجنبية × سعر اليوم', width: 26 },
                { key: 'fxValue', label: 'بسعر اليوم', kind: 'money' },
              ] as TableExportColumn[])
            : []),
        ],
        rows: d.rows.map((r) => ({
          code: r.code,
          name: r.name,
          type: LEDGER_TYPE_MAP[r.type]?.label ?? r.type,
          debit: r.debit,
          credit: r.credit,
          balance: r.balance,
          ...(anyFx ? { fx: fxText(r.fx), fxValue: r.fx?.value ?? null } : {}),
        })),
        totals: { code: 'الإجمالي', debit: d.totals.debit, credit: d.totals.credit },
      },
    ],
    notes: [
      d.totals.balanced
        ? 'الميزان متوازن: مجموع الأرصدة المدينة يساوي الدائنة.'
        : 'الميزان غير متوازن — خلل في الترحيل يحتاج مراجعة.',
    ],
  };
}

/** ورقةُ قائمةٍ بعينها — تُجلب بياناتُها كما تجلبها الشاشة (أو تُعطى إن كانت معروضة). */
export async function statementSheet(
  s: FinancialStatement,
  from: string,
  to: string,
  loaded?: { income?: IncomeStatementData; balance?: BalanceSheetData; trial?: TrialBalanceData },
): Promise<TableExportSheet> {
  if (s === 'income') {
    const d =
      loaded?.income ??
      (await api<IncomeStatementData>(
        `/accounting/reports/income-statement${qs({ from: isoDay(from), to: isoDay(to) })}`,
      ));
    return incomeSheet(d, from, to);
  }
  if (s === 'balance') {
    const d =
      loaded?.balance ??
      (await api<BalanceSheetData>(`/accounting/reports/balance-sheet${qs({ asOf: isoDay(to) })}`));
    return balanceSheetSheet(d, to);
  }
  const d =
    loaded?.trial ?? (await api<TrialBalanceData>(`/accounting/reports/trial-balance${qs({ to: isoDay(to) })}`));
  return trialSheet(d, to);
}

/** مستندُ التصدير: قائمةٌ بعينها أو الثلاث معًا (ورقةٌ/صفحةٌ لكلّ قائمة). */
export async function statementsDoc(
  which: FinancialStatement | 'all',
  from: string,
  to: string,
  loaded?: Parameters<typeof statementSheet>[3],
): Promise<TableExportDoc> {
  const list: FinancialStatement[] = which === 'all' ? ['income', 'balance', 'trial'] : [which];
  const sheets = await Promise.all(list.map((s) => statementSheet(s, from, to, loaded)));
  return {
    title: `${which === 'all' ? 'القوائم المالية' : STATEMENT_TITLES[which]} ${to}`,
    sheets,
  };
}
